import { formatPrice } from './strategyTesterEngine';

// Deriv's current public market-data endpoint. No app_id, no authentication. The older
// `ws.derivws.com/websockets/v3?app_id=…` endpoint still serves history but refuses every live
// tick subscription, which is what forced this feed to poll before.
const DERIV_WS_URL = 'wss://api.derivws.com/trading/v1/options/ws/public';
const LOCAL_HOST_PATTERN = /^(localhost|127\.0\.0\.1)$/i;
const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && LOCAL_HOST_PATTERN.test(window.location.hostname)) {
    return 'http://localhost:5000';
  }
  return '';
})();

async function requestTester(path, options = {}) {
  const { query = {}, method = 'GET', body, token } = options;
  const url = new URL(`${API_BASE_URL}${path}`, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  const headers = {};
  if (body) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || 'Builder market-data request failed.');
    error.status = response.status;
    if (payload.code) error.code = payload.code;
    if (payload.dataConfidence) error.dataConfidence = payload.dataConfidence;
    throw error;
  }
  return payload;
}

export const DATA_SOURCE_OPTIONS = [
  { id: 'deriv', label: 'Deriv', detail: 'Server-backed historical data with live ticks.' },
];

export const BROKER_PROFILE_OPTIONS = [
  {
    id: 'deriv-svg-server-03',
    label: 'Provider Specs',
    detail: 'Contract size, volume, spread, stops, swap and margin assumptions.',
    defaultFor: ['deriv'],
  },
];

export function getDefaultBrokerProfileId() {
  return 'deriv-svg-server-03';
}

export function getBrokerProfileLabel(profileId = '') {
  return BROKER_PROFILE_OPTIONS.find((profile) => profile.id === profileId)?.label || 'Provider Specs';
}

export function formatBrokerSpecConfidence(spec) {
  const confidence = String(spec?.specConfidence || 'unknown').replace(/_/g, ' ');
  const missingCount = Array.isArray(spec?.missingFields) ? spec.missingFields.length : 0;
  return missingCount ? `${confidence} (${missingCount} missing)` : confidence;
}

export async function fetchServerSymbolCatalog(provider = 'deriv') {
  return requestTester('/api/tester/catalog', { query: { provider } });
}

export async function fetchBrokerProfiles() {
  return requestTester('/api/tester/broker-profiles');
}

export async function fetchServerCandles({
  provider = 'deriv',
  uiSymbol,
  timeframe,
  from,
  to,
  maxBars = 1800,
  startEpoch = null,
  endEpoch = null,
  brokerProfileId = '',
  modelingMode = '',
  cacheOnly = false,
}) {
  return requestTester('/api/tester/history', {
    query: {
      provider,
      symbol: uiSymbol,
      timeframe,
      from,
      to,
      maxBars,
      startEpoch,
      endEpoch,
      brokerProfileId,
      modelingMode,
      cacheOnly: cacheOnly ? '1' : '',
    },
  });
}

export async function runServerBacktest({ setup, indicators, rules, sessions, maxChartBars = 1600 }) {
  return requestTester('/api/tester/backtest', {
    method: 'POST',
    body: {
      setup,
      indicators,
      rules,
      sessions,
      maxChartBars,
    },
  });
}

export async function startServerBackfillJob({ provider, symbol, timeframe, from, to, refresh = false }) {
  return requestTester('/api/tester/backfill', {
    method: 'POST',
    body: {
      provider,
      symbol,
      timeframe,
      from,
      to,
      refresh,
    },
  });
}

export async function fetchServerBackfillJob(jobId) {
  return requestTester(`/api/tester/backfill/${jobId}`);
}

export async function cancelServerBackfillJob(jobId) {
  return requestTester(`/api/tester/backfill/${jobId}/cancel`, {
    method: 'POST',
  });
}

export async function fetchServerCoverage({ provider, symbol, timeframe }) {
  return requestTester('/api/tester/coverage', {
    query: {
      provider,
      symbol,
      timeframe,
    },
  });
}

export async function startServerBacktestJob({ setup, indicators, rules, sessions, maxChartBars = 1600, pageSize = 50000 }) {
  return requestTester('/api/tester/backtest-jobs', {
    method: 'POST',
    body: {
      setup,
      indicators,
      rules,
      sessions,
      maxChartBars,
      pageSize,
    },
  });
}

export async function fetchServerBacktestJob(jobId) {
  return requestTester(`/api/tester/backtest-jobs/${jobId}`);
}

export async function cancelServerBacktestJob(jobId) {
  return requestTester(`/api/tester/backtest-jobs/${jobId}/cancel`, {
    method: 'POST',
  });
}

export async function startOptimization({ strategy, parameters, metric, minTrades }) {
  return requestTester('/api/tester/optimizations', {
    method: 'POST',
    body: { strategy, parameters, metric, minTrades },
  });
}

export async function fetchOptimization(jobId) {
  return requestTester(`/api/tester/optimizations/${jobId}`);
}

export async function cancelOptimization(jobId) {
  return requestTester(`/api/tester/optimizations/${jobId}/cancel`, { method: 'POST' });
}

export async function startWalkForward({ strategy, parameters, metric, minTrades, windows }) {
  return requestTester('/api/tester/walk-forward', {
    method: 'POST',
    body: { strategy, parameters, metric, minTrades, windows },
  });
}

export async function fetchWalkForward(jobId) {
  return requestTester(`/api/tester/walk-forward/${jobId}`);
}

export async function cancelWalkForward(jobId) {
  return requestTester(`/api/tester/walk-forward/${jobId}/cancel`, { method: 'POST' });
}

export async function sampleSymbolSpreads() {
  return requestTester('/api/tester/spreads/sample', { method: 'POST' });
}

export async function listSavedTesterStrategies(token) {
  return requestTester('/api/tester/strategies', { token });
}

export async function fetchTesterUserProfile(token) {
  return requestTester('/api/auth/me', { token });
}

export async function fetchSavedTesterStrategy(strategyId, token) {
  return requestTester(`/api/tester/strategies/${strategyId}`, { token });
}

export async function createSavedTesterStrategy(payload, token) {
  return requestTester('/api/tester/strategies', {
    method: 'POST',
    body: payload,
    token,
  });
}

export async function updateSavedTesterStrategy(strategyId, payload, token) {
  return requestTester(`/api/tester/strategies/${strategyId}`, {
    method: 'PUT',
    body: payload,
    token,
  });
}

export async function deleteSavedTesterStrategy(strategyId, token) {
  return requestTester(`/api/tester/strategies/${strategyId}`, {
    method: 'DELETE',
    token,
  });
}

export async function exportSavedTesterStrategyEa(strategyId, token) {
  return requestTester(`/api/tester/strategies/${strategyId}/export-ea`, {
    token,
  });
}

export async function exportStrategyEa(payload, token) {
  return requestTester('/api/tester/export-ea', {
    method: 'POST',
    body: payload,
    token,
  });
}

function formatLoadingRange(marketState) {
  if (!marketState?.loadingFrom || !marketState?.loadingTo) {
    return '';
  }
  return `${marketState.loadingFrom} to ${marketState.loadingTo}`;
}

function formatLoadingTarget(marketState) {
  const parts = [marketState?.loadingSymbol, marketState?.loadingTimeframe].filter(Boolean);
  return parts.length ? parts.join(' ') : '';
}

function formatLoadingElapsed(startedAt) {
  if (!startedAt) {
    return '';
  }
  const elapsedMs = Date.now() - new Date(startedAt).getTime();
  if (!Number.isFinite(elapsedMs) || elapsedMs < 1000) {
    return '';
  }
  const seconds = Math.round(elapsedMs / 1000);
  if (seconds < 60) {
    return `${seconds}s elapsed`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s elapsed`;
}

export function describeMarketDataSource(sourceId, marketState) {
  if (sourceId === 'deriv') {
    if (marketState.status === 'loading') {
      const targetLabel = formatLoadingTarget(marketState);
      const rangeLabel = formatLoadingRange(marketState);
      const elapsedLabel = formatLoadingElapsed(marketState.loadingStartedAt);
      return `Loading preview history${targetLabel ? ` for ${targetLabel}` : ''}${rangeLabel ? `, ${rangeLabel}` : ''}${elapsedLabel ? ` (${elapsedLabel})` : ''}...`;
    }
    if (marketState.status === 'error') {
      return 'History could not load. Check your connection and retry.';
    }
    if (marketState.status === 'ready') {
      const providerCode = marketState.providerSymbol ? ` (${marketState.providerSymbol})` : '';
      // Deliberately says nothing about the live feed any more. This used to read "Live ticks
      // connected separately", which was printed whenever the subscription GATE passed — a
      // statement about configuration dressed up as a statement about data, and it read as a
      // confirmation even when not one tick had arrived. Feed health is now reported by
      // LiveFeedIndicator, which only claims "Live" once ticks are actually landing.
      const liveText = marketState.liveTicksEnabled === false ? 'Historical range only.' : '';
      return `History ready${providerCode}.${liveText ? ` ${liveText}` : ''}`;
    }
    return 'Market data selected.';
  }

  return 'Market data selected.';
}

export function formatMarketTimestamp(value) {
  if (!value) {
    return 'Not loaded';
  }
  return new Date(value).toLocaleString();
}

export function formatProviderPrice(value, digits) {
  return formatPrice(value, digits);
}

// A real tick subscription — push, not poll.
//
// This used to poll `ticks_history` every 4 seconds because `{ ticks, subscribe: 1 }` was rejected
// with InvalidSymbol for every symbol. That rejection was real, but it belonged to Deriv's OLD v3
// endpoint, not to this account or region: the same request on the current public endpoint streams
// normally, about once a second, and carries bid+ask that the polled history never had.
//
// Reconnects with backoff rather than dying, since a dropped socket used to mean a permanently
// frozen price readout.
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export function subscribeDerivTicks({ providerSymbol, onTick, onError }) {
  let socket = null;
  let reconnectTimer = null;
  let stopped = false;
  let attempts = 0;
  // Deriv replays the last value when nothing has traded; only forward genuine movement so the
  // "last tick Ns ago" readout does not claim activity that did not happen.
  let lastEpoch = 0;

  function scheduleReconnect() {
    if (stopped || reconnectTimer) return;
    const delay = Math.min(RECONNECT_BASE_MS * (2 ** attempts), RECONNECT_MAX_MS);
    attempts += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  function handleOpen() {
    attempts = 0; // a successful connection resets the backoff
    socket.send(JSON.stringify({ ticks: providerSymbol, subscribe: 1 }));
  }

  function handleMessage(event) {
    if (stopped) return;
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      onError?.(new Error('Received an invalid tick response.'));
      return;
    }

    if (data.error) {
      onError?.(new Error(data.error.message || 'Tick feed failed.'));
      return;
    }

    if (data.msg_type === 'tick' && data.tick) {
      const price = Number(data.tick.quote);
      const epoch = Number(data.tick.epoch);
      if (!Number.isFinite(price) || !Number.isFinite(epoch) || epoch <= lastEpoch) return;
      lastEpoch = epoch;
      const bid = Number(data.tick.bid);
      const ask = Number(data.tick.ask);
      onTick?.({
        price,
        // Real quotes now, where the polled feed could only ever send null and let the chart fall
        // back to the candle's own spread.
        bid: Number.isFinite(bid) ? bid : null,
        ask: Number.isFinite(ask) ? ask : null,
        time: epoch * 1000,
        symbol: providerSymbol,
      });
    }
  }

  function handleError() {
    onError?.(new Error('Could not connect to the price feed.'));
  }

  function handleClose() {
    if (!stopped) scheduleReconnect();
  }

  function cleanup() {
    if (!socket) return;
    socket.removeEventListener('open', handleOpen);
    socket.removeEventListener('message', handleMessage);
    socket.removeEventListener('error', handleError);
    socket.removeEventListener('close', handleClose);
  }

  function connect() {
    cleanup();
    socket = new WebSocket(DERIV_WS_URL);
    socket.addEventListener('open', handleOpen);
    socket.addEventListener('message', handleMessage);
    socket.addEventListener('error', handleError);
    socket.addEventListener('close', handleClose);
  }

  connect();

  return () => {
    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    cleanup();
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      socket.close();
    }
  };
}

